// Streamer-Dashboard — separates Portal für Partner-Streamer.
// Auth via Twitch OAuth (eigene Session, kein Spieler-Login).

interface DashboardSession {
  streamerId: string;
  twitchUsername: string;
  displayName: string;
}

interface DashboardStats {
  activePlayers: number;
  challengeProgress: { completed: number; total: number };
  activeEvent: { eventType: string; label: string; endsAt: string } | null;
  lastEventAt: string | null;
  canTriggerEvent: boolean;
  cooldownEndsAt: string | null;
}

interface Challenge {
  type: string;
  description: string;
  target: number;
}

interface VotingOption {
  type: string;
  label: string;
  votes: number;
}

interface VotingStatus {
  isActive: boolean;
  closesAt: string | null;
  options: VotingOption[];
}

// ─── API-Helper ───────────────────────────────────────────────────────────────

async function dashboardFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  const res = await fetch(`/dashboard/api${path}`, { ...options, headers, credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
  const body = await res.json() as { data: T };
  void method;
  return body.data;
}

// ─── State ────────────────────────────────────────────────────────────────────

let stats: DashboardStats | null = null;
let challenges: Challenge[] = [];
let voting: VotingStatus | null = null;
let session: DashboardSession | null = null;
// Partner-Spruch (NT-STREAM-PARTNERKARTEN-01). `taglineDraft` überlebt die Re-Renders des
// 30s-Pollings, damit ein halb getippter Text nicht verschwindet.
interface TaglineView { tagline: string | null; effective: string }
const TAGLINE_MAX_LENGTH = 120; // Spiegel von PARTNER_TAGLINE_MAX_LENGTH (Backend ist maßgeblich)
let taglineView: TaglineView | null = null;
let taglineDraft: string | null = null;
let pollInterval: ReturnType<typeof setInterval> | null = null;
let countdownInterval: ReturnType<typeof setInterval> | null = null;

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  const existing = document.getElementById('streamer-dashboard-styles');
  const el = existing ?? document.createElement('style');
  el.id = 'streamer-dashboard-styles';
  el.textContent = `
    .sd-page {
      max-width: 960px;
      margin: 0 auto;
      padding: 1.5rem 1rem 4rem;
      font-family: 'Share Tech Mono', 'Courier New', monospace;
      color: var(--text-primary);
    }
    .sd-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--border);
      padding-bottom: 1rem;
      margin-bottom: 1.5rem;
    }
    .sd-title {
      font-size: var(--font-size-xl);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: var(--accent-green);
    }
    .sd-user {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-transform: uppercase;
    }
    .sd-logout {
      background: none;
      border: 1px solid var(--border);
      color: var(--text-muted);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      padding: 0.35rem 0.75rem;
      border-radius: var(--radius-btn);
      cursor: pointer;
      letter-spacing: 0.05em;
    }
    .sd-logout:hover { color: var(--accent-red); border-color: var(--accent-red); }
    .sd-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    @media (max-width: 680px) { .sd-grid { grid-template-columns: 1fr; } }
    .sd-card {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.25rem 1.5rem;
    }
    .sd-card-title {
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      color: var(--text-muted);
      letter-spacing: 0.08em;
      margin-bottom: 1rem;
      border-bottom: 1px solid var(--border);
      padding-bottom: 0.5rem;
    }
    .sd-stat-label {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-transform: uppercase;
      margin-top: 0.75rem;
    }
    .sd-stat-value {
      font-size: var(--font-size-lg);
      color: var(--text-primary);
    }
    .sd-stat-value.accent-green { color: var(--accent-green); }
    .sd-stat-value.accent-gold  { color: var(--accent-gold); }
    .sd-stat-value.accent-red   { color: var(--accent-red); }
    .sd-btn {
      display: inline-block;
      background: var(--bg-elevated);
      border: 1px solid var(--accent-green);
      color: var(--accent-green);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      padding: 0.5rem 1rem;
      border-radius: var(--radius-btn);
      cursor: pointer;
      letter-spacing: 0.05em;
      margin-top: 0.75rem;
    }
    .sd-btn:hover:not(:disabled) { background: var(--accent-green); color: var(--bg-base); }
    .sd-btn:disabled { opacity: 0.4; cursor: not-allowed; border-color: var(--border); color: var(--text-muted); }
    .sd-btn.red   { border-color: var(--accent-red);    color: var(--accent-red); }
    .sd-btn.red:hover:not(:disabled)   { background: var(--accent-red);    color: var(--bg-base); }
    .sd-btn.gold  { border-color: var(--accent-gold);   color: var(--accent-gold); }
    .sd-btn.gold:hover:not(:disabled)  { background: var(--accent-gold);   color: var(--bg-base); }
    .sd-btn.blue  { border-color: var(--accent-blue);   color: var(--accent-blue); }
    .sd-btn.blue:hover:not(:disabled)  { background: var(--accent-blue);   color: var(--bg-base); }
    .sd-select {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-xs);
      padding: 0.4rem 0.6rem;
      border-radius: var(--radius);
      width: 100%;
      margin-top: 0.5rem;
    }
    .sd-select:focus { outline: none; border-color: var(--accent-blue); }
    .sd-text-input {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-xs);
      padding: 0.4rem 0.6rem;
      border-radius: var(--radius);
      width: 100%;
      box-sizing: border-box;
      margin-top: 0.5rem;
    }
    .sd-text-input:focus { outline: none; border-color: var(--accent-blue); }
    .sd-tagline-count { font-size: var(--font-size-xs); color: var(--text-muted); margin-top: 0.25rem; }
    .sd-duration-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-top: 0.75rem;
    }
    .sd-duration-label {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-transform: uppercase;
      white-space: nowrap;
    }
    .sd-duration-input {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-xs);
      padding: 0.4rem 0.6rem;
      border-radius: var(--radius);
      width: 80px;
    }
    .sd-duration-input:focus { outline: none; border-color: var(--accent-blue); }
    .sd-vote-bar {
      margin-top: 0.5rem;
    }
    .sd-vote-label {
      display: flex;
      justify-content: space-between;
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-transform: uppercase;
      margin-bottom: 0.2rem;
    }
    .sd-vote-track {
      height: 6px;
      background: var(--bg-elevated);
      border-radius: 3px;
      overflow: hidden;
    }
    .sd-vote-fill {
      height: 100%;
      background: var(--accent-green);
      border-radius: 3px;
      transition: width 0.3s;
    }
    .sd-event-active {
      font-size: var(--font-size-base);
      color: var(--accent-gold);
      text-transform: uppercase;
      font-weight: bold;
      margin-bottom: 0.5rem;
    }
    .sd-countdown {
      font-size: var(--font-size-sm);
      color: var(--accent-green);
      font-variant-numeric: tabular-nums;
    }
    .sd-cooldown-note {
      font-size: var(--font-size-xs);
      color: var(--accent-red);
      text-transform: uppercase;
      margin-top: 0.5rem;
    }
    .sd-login-page {
      max-width: 480px;
      margin: 6rem auto;
      text-align: center;
      font-family: 'Share Tech Mono', 'Courier New', monospace;
      color: var(--text-primary);
      padding: 0 1rem;
    }
    .sd-login-title {
      font-size: var(--font-size-xl);
      text-transform: uppercase;
      color: var(--accent-green);
      margin-bottom: 1rem;
      letter-spacing: 0.1em;
    }
    .sd-login-sub {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-transform: uppercase;
      margin-bottom: 2rem;
    }
    .sd-error {
      font-size: var(--font-size-xs);
      color: var(--accent-red);
      text-transform: uppercase;
      margin-bottom: 1rem;
    }
    .sd-full {
      grid-column: 1 / -1;
    }
  `;
  if (!existing) document.head.appendChild(el);
}

// ─── Render: Login-Screen ─────────────────────────────────────────────────────

function renderLogin(errorCode?: string): void {
  const app = document.getElementById('app');
  if (!app) return;

  const errorMessages: Record<string, string> = {
    oauth_denied:    'TWITCH-AUTHORISIERUNG ABGEBROCHEN.',
    invalid_callback: 'UNGÜLTIGER CALLBACK. ERNEUT VERSUCHEN.',
    not_authorized:  'DEIN TWITCH-ACCOUNT IST KEIN PARTNER-STREAMER.',
  };

  const errorHtml = errorCode
    ? `<div class="sd-error">${errorMessages[errorCode] ?? 'UNBEKANNTER FEHLER.'}</div>`
    : '';

  app.innerHTML = `
    <div class="sd-login-page">
      <div class="sd-login-title">NIGHTTRADE STREAMER-DASHBOARD</div>
      <div class="sd-login-sub">Nur für Partner-Streamer — Login mit Twitch</div>
      ${errorHtml}
      <a href="/dashboard/auth/twitch/login" class="sd-btn gold" style="text-decoration:none; display:inline-block;">
        MIT TWITCH EINLOGGEN
      </a>
    </div>
  `;
}

// ─── Render: Haupt-Dashboard ──────────────────────────────────────────────────

function renderDashboard(): void {
  if (!stats || !session) return;
  const app = document.getElementById('app');
  if (!app) return;

  const s = stats;
  const challengeProgress = s.challengeProgress.total > 0
    ? Math.round((s.challengeProgress.completed / s.challengeProgress.total) * 100)
    : 0;

  // Event-Panel
  let eventHtml = '';
  if (s.activeEvent) {
    eventHtml = `
      <div class="sd-event-active">${s.activeEvent.label}</div>
      <div class="sd-stat-label">ENDET IN</div>
      <div class="sd-countdown" id="sd-event-countdown">—</div>
    `;
  } else {
    const cooldownHtml = s.cooldownEndsAt
      ? `<div class="sd-cooldown-note">COOLDOWN: <span class="sd-countdown" id="sd-cooldown-countdown">—</span></div>`
      : '';

    eventHtml = `
      <div class="sd-stat-label">KEIN AKTIVES EVENT</div>
      ${cooldownHtml}
      <div style="margin-top:0.75rem; display:flex; gap:0.5rem; flex-wrap:wrap;">
        <button class="sd-btn gold" id="sd-btn-trigger" ${!s.canTriggerEvent ? 'disabled' : ''}>
          EVENT TRIGGERN
        </button>
        <button class="sd-btn blue" id="sd-btn-start-voting" ${!s.canTriggerEvent ? 'disabled' : ''}>
          ABSTIMMUNG STARTEN
        </button>
      </div>
    `;
  }

  // Voting-Panel
  let votingHtml = '';
  if (voting?.isActive && voting.options.length > 0) {
    const totalVotes = voting.options.reduce((s, o) => s + o.votes, 0);
    const barsHtml = voting.options.map(opt => {
      const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
      return `
        <div class="sd-vote-bar">
          <div class="sd-vote-label">
            <span>${opt.label}</span>
            <span>${opt.votes} STIMMEN</span>
          </div>
          <div class="sd-vote-track"><div class="sd-vote-fill" style="width:${pct}%"></div></div>
        </div>
      `;
    }).join('');

    votingHtml = `
      <div class="sd-stat-label">STATUS: AKTIVE ABSTIMMUNG</div>
      ${barsHtml}
      <div class="sd-stat-label" style="margin-top:0.75rem;">ENDET IN</div>
      <div class="sd-countdown" id="sd-voting-countdown">—</div>
      <button class="sd-btn red" id="sd-btn-close-voting" style="margin-top:0.75rem;">ABSTIMMUNG SCHLIESSEN</button>
    `;
  } else {
    votingHtml = `
      <div class="sd-stat-label">STATUS: KEINE AKTIVE ABSTIMMUNG</div>
      <div class="sd-duration-row">
        <span class="sd-duration-label">DAUER (MIN)</span>
        <input type="number" class="sd-duration-input" id="sd-voting-duration" value="60" min="5" max="240">
      </div>
      <button class="sd-btn blue" id="sd-btn-start-voting2" ${!s.canTriggerEvent ? 'disabled' : ''}>
        ABSTIMMUNG STARTEN
      </button>
    `;
  }

  // Challenge-Panel
  const challengeOptions = challenges.map(c =>
    `<option value="${escHtml(c.type)}">${escHtml(c.description)}</option>`
  ).join('');

  app.innerHTML = `
    <div class="sd-page">
      <div class="sd-header">
        <div>
          <div class="sd-title">NIGHTTRADE STREAMER-DASHBOARD</div>
          <div class="sd-user">EINGELOGGT ALS: ${escHtml(session.displayName)} (@${escHtml(session.twitchUsername)})</div>
        </div>
        <button class="sd-logout" id="sd-btn-logout">AUSLOGGEN</button>
      </div>

      <div class="sd-grid">
        <!-- Community Stats -->
        <div class="sd-card">
          <div class="sd-card-title">COMMUNITY STATS</div>
          <div class="sd-stat-label">AKTIVE SPIELER (LETZTE STUNDE)</div>
          <div class="sd-stat-value accent-green">${s.activePlayers}</div>
          <div class="sd-stat-label">CHALLENGE HEUTE</div>
          <div class="sd-stat-value">${s.challengeProgress.completed} / ${s.challengeProgress.total} (${challengeProgress}%)</div>
          <div class="sd-stat-label">LETZTES EVENT</div>
          <div class="sd-stat-value">${s.lastEventAt ? formatRelativeTime(s.lastEventAt) : '—'}</div>
        </div>

        <!-- Aktives Event -->
        <div class="sd-card" id="sd-event-card">
          <div class="sd-card-title">AKTIVES EVENT</div>
          ${eventHtml}
        </div>

        <!-- Tages-Challenge -->
        <div class="sd-card sd-full">
          <div class="sd-card-title">TAGES-CHALLENGE AUSWÄHLEN <span style="color:var(--text-muted);font-size:var(--font-size-xs)">(NUR WENN DU LIVE BIST)</span></div>
          <select class="sd-select" id="sd-challenge-select">
            <option value="">— CHALLENGE WÄHLEN —</option>
            ${challengeOptions}
          </select>
          <button class="sd-btn" id="sd-btn-challenge" style="margin-left:0.5rem;">CHALLENGE SETZEN</button>
        </div>

        <!-- Partner-Spruch -->
        <div class="sd-card sd-full">
          <div class="sd-card-title">DEIN SPRUCH <span style="color:var(--text-muted);font-size:var(--font-size-xs)">(ERSCHEINT AUF DEINER PARTNER-KARTE)</span></div>
          <input type="text" class="sd-text-input" id="sd-tagline-input"
                 maxlength="${TAGLINE_MAX_LENGTH}" autocomplete="off"
                 placeholder="${escHtml(taglineView?.effective ?? '')}"
                 value="${escHtml(taglineDraft ?? taglineView?.tagline ?? '')}">
          <div class="sd-tagline-count" id="sd-tagline-count"></div>
          <div class="sd-stat-label" style="margin-top:0.5rem;">AKTUELL ANGEZEIGT: ${escHtml(taglineView?.effective ?? '—')}${taglineView?.tagline === null ? ' (STANDARDSATZ)' : ''}</div>
          <div style="margin-top:0.75rem; display:flex; gap:0.5rem; flex-wrap:wrap;">
            <button class="sd-btn gold" id="sd-btn-tagline-save">SPEICHERN</button>
            <button class="sd-btn" id="sd-btn-tagline-reset">ZURÜCKSETZEN</button>
          </div>
        </div>

        <!-- Abstimmung -->
        <div class="sd-card sd-full">
          <div class="sd-card-title">ABSTIMMUNG</div>
          ${votingHtml}
        </div>
      </div>

      <!-- Event direkt triggern (Modal-ähnlich, inline) -->
      <div class="sd-card" id="sd-trigger-panel" style="display:none;">
        <div class="sd-card-title">EVENT DIREKT TRIGGERN</div>
        <select class="sd-select" id="sd-event-select">
          ${Object.entries(EVENT_LABELS_FRONTEND).map(([type, label]) =>
            `<option value="${type}">${label}</option>`
          ).join('')}
        </select>
        <div style="margin-top:0.75rem; display:flex; gap:0.5rem;">
          <button class="sd-btn gold" id="sd-btn-trigger-confirm">TRIGGERN</button>
          <button class="sd-btn" id="sd-btn-trigger-cancel">ABBRECHEN</button>
        </div>
      </div>
    </div>
  `;

  bindEvents();
  startCountdowns();
}

const EVENT_LABELS_FRONTEND: Record<string, string> = {
  NACHT_CREW_AKTIV:     'NACHT-CREW AKTIV — +50% Streampoints',
  AMNESTIE:             'AMNESTIE — Kein FL-Aufbau',
  GOLDENE_STUNDE:       'GOLDENE STUNDE — Job-XP x2',
  SCHWARZMARKT_OFFEN:   'SCHWARZMARKT OFFEN — +20% illegale Warenpreise',
  TURBO_NACHT:          'TURBO-NACHT — Transport -50% Zeit',
  EXPRESS_ERNTE:        'EXPRESS-ERNTE — Pflanzenwachstum +100%',
  FREIE_FAHRT:          'FREIE FAHRT — Kein Job-Cooldown',
  SCHWARZER_FREITAG:    'SCHWARZER FREITAG — Alle Marktpreise +25%',
  GELDWAESCHE_FESTIVAL: 'GELDWÄSCHE-FESTIVAL — Verlust halbiert',
  DEALER_RUSH:          'DEALER-RUSH — +40% Dealer-Einnahmen',
  POLIZEI_STREIK:       'POLIZEI-STREIK — Kein FL-Aufbau alle Jobs',
  RAZZIA_PAUSE:         'RAZZIA-PAUSE — Keine Razzien',
  STOSSZEIT:            'STOSSZEIT — Dealer-Zyklen -33% Zeit',
  INFORMANTEN_NETZ:     'INFORMANTEN-NETZ — Razzia-Vorwarnung 60 Min',
  NACHT_DER_KONTAKTE:   'NACHT DER KONTAKTE — NPC-Kontakte ohne Wartezeit',
};

// ─── Event-Handler ────────────────────────────────────────────────────────────

function bindEvents(): void {
  document.getElementById('sd-btn-logout')?.addEventListener('click', () => void handleLogout());
  document.getElementById('sd-btn-trigger')?.addEventListener('click', () => {
    const panel = document.getElementById('sd-trigger-panel');
    if (panel) panel.style.display = 'block';
  });
  document.getElementById('sd-btn-trigger-cancel')?.addEventListener('click', () => {
    const panel = document.getElementById('sd-trigger-panel');
    if (panel) panel.style.display = 'none';
  });
  document.getElementById('sd-btn-trigger-confirm')?.addEventListener('click', () => void handleTriggerEvent());
  document.getElementById('sd-btn-start-voting')?.addEventListener('click', () => void handleStartVoting());
  document.getElementById('sd-btn-start-voting2')?.addEventListener('click', () => void handleStartVoting());
  document.getElementById('sd-btn-close-voting')?.addEventListener('click', () => void handleCloseVoting());
  document.getElementById('sd-btn-challenge')?.addEventListener('click', () => void handleSelectChallenge());

  const taglineInput = document.getElementById('sd-tagline-input');
  const updateCount = (): void => {
    const el = document.getElementById('sd-tagline-count');
    if (el && taglineInput instanceof HTMLInputElement) {
      el.textContent = `${String(taglineInput.value.length)} / ${String(TAGLINE_MAX_LENGTH)}`;
    }
  };
  taglineInput?.addEventListener('input', () => {
    if (taglineInput instanceof HTMLInputElement) taglineDraft = taglineInput.value;
    updateCount();
  });
  updateCount();
  document.getElementById('sd-btn-tagline-save')?.addEventListener('click', () => void handleSaveTagline());
  document.getElementById('sd-btn-tagline-reset')?.addEventListener('click', () => void handleResetTagline());
}

async function handleSaveTagline(): Promise<void> {
  const input = document.getElementById('sd-tagline-input');
  if (!(input instanceof HTMLInputElement)) return;
  const text = input.value.trim();
  if (text.length === 0) { alert('DER SPRUCH DARF NICHT LEER SEIN. ZUM ENTFERNEN: ZURÜCKSETZEN.'); return; }
  const btn = document.getElementById('sd-btn-tagline-save') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'SPEICHERT...'; }
  try {
    taglineView = await dashboardFetch<TaglineView>('/tagline', {
      method: 'POST',
      body: JSON.stringify({ tagline: text }),
    });
    taglineDraft = null;
    renderDashboard();
  } catch (e) {
    alert(`FEHLER: ${(e as Error).message}`);
    if (btn) { btn.disabled = false; btn.textContent = 'SPEICHERN'; }
  }
}

async function handleResetTagline(): Promise<void> {
  const btn = document.getElementById('sd-btn-tagline-reset') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'SETZT ZURÜCK...'; }
  try {
    taglineView = await dashboardFetch<TaglineView>('/tagline/reset', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    taglineDraft = null;
    renderDashboard();
  } catch (e) {
    alert(`FEHLER: ${(e as Error).message}`);
    if (btn) { btn.disabled = false; btn.textContent = 'ZURÜCKSETZEN'; }
  }
}

async function handleLogout(): Promise<void> {
  try {
    await fetch('/dashboard/auth/logout', { method: 'DELETE', credentials: 'include' });
  } catch { /* ignorieren */ }
  session = null;
  taglineView = null;
  taglineDraft = null;
  clearIntervals();
  renderLogin();
}

async function handleTriggerEvent(): Promise<void> {
  const sel = document.getElementById('sd-event-select') as HTMLSelectElement | null;
  const eventType = sel?.value;
  if (!eventType) return;

  const btn = document.getElementById('sd-btn-trigger-confirm') as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = 'TRIGGERT...';
  try {
    await dashboardFetch('/event/trigger', {
      method: 'POST',
      body: JSON.stringify({ eventType }),
    });
    await refreshData();
  } catch (e) {
    alert(`FEHLER: ${(e as Error).message}`);
  } finally {
    btn.disabled = false;
    btn.textContent = 'TRIGGERN';
    const panel = document.getElementById('sd-trigger-panel');
    if (panel) panel.style.display = 'none';
  }
}

async function handleStartVoting(): Promise<void> {
  const durInput = document.getElementById('sd-voting-duration') as HTMLInputElement | null;
  const durationMinutes = durInput ? parseInt(durInput.value, 10) : 60;
  if (isNaN(durationMinutes) || durationMinutes < 5 || durationMinutes > 240) {
    alert('DAUER: 5 BIS 240 MINUTEN.');
    return;
  }
  const btn = document.getElementById('sd-btn-start-voting') ?? document.getElementById('sd-btn-start-voting2');
  if (btn instanceof HTMLButtonElement) { btn.disabled = true; btn.textContent = 'STARTET...'; }
  try {
    await dashboardFetch('/voting/start', { method: 'POST', body: JSON.stringify({ durationMinutes }) });
    await refreshData();
  } catch (e) {
    alert(`FEHLER: ${(e as Error).message}`);
    if (btn instanceof HTMLButtonElement) { btn.disabled = false; btn.textContent = 'ABSTIMMUNG STARTEN'; }
  }
}

async function handleCloseVoting(): Promise<void> {
  const btn = document.getElementById('sd-btn-close-voting') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'SCHLIESSEN...'; }
  try {
    await dashboardFetch('/voting/close', { method: 'POST', body: JSON.stringify({}) });
    await refreshData();
  } catch (e) {
    alert(`FEHLER: ${(e as Error).message}`);
    if (btn) { btn.disabled = false; btn.textContent = 'ABSTIMMUNG SCHLIESSEN'; }
  }
}

async function handleSelectChallenge(): Promise<void> {
  const sel = document.getElementById('sd-challenge-select') as HTMLSelectElement | null;
  const challengeType = sel?.value;
  if (!challengeType) return;
  const btn = document.getElementById('sd-btn-challenge') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'SETZT...'; }
  try {
    await dashboardFetch('/challenge/select', { method: 'POST', body: JSON.stringify({ challengeType }) });
    alert('CHALLENGE GESETZT.');
  } catch (e) {
    alert(`FEHLER: ${(e as Error).message}`);
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'CHALLENGE SETZEN'; }
  }
}

// ─── Countdowns ───────────────────────────────────────────────────────────────

function startCountdowns(): void {
  if (countdownInterval) clearInterval(countdownInterval);
  countdownInterval = setInterval(tickCountdowns, 1000);
  tickCountdowns();
}

function tickCountdowns(): void {
  if (stats?.activeEvent?.endsAt) {
    const el = document.getElementById('sd-event-countdown');
    if (el) el.textContent = formatCountdown(stats.activeEvent.endsAt);
  }
  if (stats?.cooldownEndsAt) {
    const el = document.getElementById('sd-cooldown-countdown');
    if (el) el.textContent = formatCountdown(stats.cooldownEndsAt);
  }
  if (voting?.isActive && voting.closesAt) {
    const el = document.getElementById('sd-voting-countdown');
    if (el) el.textContent = formatCountdown(voting.closesAt);
  }
}

function formatCountdown(isoDate: string): string {
  const remaining = new Date(isoDate).getTime() - Date.now();
  if (remaining <= 0) return '00:00:00';
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function pad(n: number): string { return String(n).padStart(2, '0'); }

// ─── Polling ──────────────────────────────────────────────────────────────────

async function refreshData(): Promise<void> {
  [stats, challenges, voting, taglineView] = await Promise.all([
    dashboardFetch<DashboardStats>('/stats'),
    dashboardFetch<Challenge[]>('/challenges'),
    dashboardFetch<VotingStatus>('/voting'),
    dashboardFetch<TaglineView>('/tagline'),
  ]);
  renderDashboard();
}

function clearIntervals(): void {
  if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
  if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'VOR WENIGER ALS EINER STUNDE';
  if (h === 1) return 'VOR 1 STUNDE';
  return `VOR ${h} STUNDEN`;
}

// ─── Entry-Point ──────────────────────────────────────────────────────────────

export async function renderStreamerDashboard(): Promise<void> {
  addStyles();

  // URL-Error-Parameter auslesen (nach OAuth-Redirect)
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('error') ?? undefined;

  // Session prüfen
  try {
    session = await dashboardFetch<DashboardSession>('/auth/me').catch(() => null);
  } catch {
    session = null;
  }

  // Wenn keine Session: Login-Screen zeigen
  if (!session) {
    renderLogin(errorCode);
    return;
  }

  // Fehler-URL-Parameter entfernen
  if (errorCode) {
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.toString());
  }

  // Daten laden + rendern
  try {
    await refreshData();
  } catch {
    session = null;
    renderLogin();
    return;
  }

  // Polling alle 30s
  clearIntervals();
  pollInterval = setInterval(() => void refreshData(), 30_000);
}
